import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, HelpCircle, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  { q: 'How do I register on the platform?',     a: 'Click "Get Started" on the homepage and fill in your details. You must be 18 or older to register.' },
  { q: 'Is the platform free to use?',            a: 'Registration is free. We charge a small commission only when you successfully complete a job through our platform.' },
  { q: 'How are payments handled?',               a: 'We use an escrow-based payment system. Payments are released only when both parties are satisfied with the work.' },
  { q: 'How do I verify my account?',             a: 'After registration, verify your account via OTP sent to your phone number.' },
  { q: 'What if I face issues with a job?',       a: 'Report issues through the complaint section. Our admin team resolves matters within 24-48 hours.' },
  { q: 'Can I work remotely?',                    a: 'Yes! Many jobs support remote work. You can filter jobs by location or select the "Remote" option.' },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '', feedbackType: 'general' });
  const [submitted, setSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '', feedbackType: 'general' });
    }, 3000);
  };

  const contactCards = [
    { icon: <Mail className="w-5 h-5" />, title: 'Email Us', info: 'Get support via email', lines: ['support@mattersurskills.com', 'contact@mattersurskills.com'] },
    { icon: <Phone className="w-5 h-5" />, title: 'Call Us', info: 'Mon-Fri from 9am to 6pm', lines: ['+91 98765 43210', '+91 98765 43211'] },
    { icon: <MapPin className="w-5 h-5" />, title: 'Visit Us', info: 'Our main office location', lines: ['123 Tech Park, Cyber City', 'Gurugram, Haryana - 122001'] },
  ];

  return (
    <div className="min-h-screen font-sans" style={{ background: 'var(--background)' }}>

      {/* Page Header */}
      <div className="border-b pt-24 pb-16" style={{ background: 'var(--muted)', borderColor: 'var(--border)' }}>
        <div className="section-container text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <span className="text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full border"
              style={{ background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
              Contact
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl sm:text-4xl font-extrabold mt-6 mb-3" style={{ color: 'var(--foreground)' }}>
            Get in touch
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm max-w-lg mx-auto leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Have questions about findings jobs, payments, or verification? Send us a message and we'll respond as soon as possible.
          </motion.p>
        </div>
      </div>

      {/* Contact Cards Section */}
      <section className="py-16 border-b" style={{ background: 'var(--background)', borderColor: 'var(--border)' }}>
        <div className="section-container">
          <div className="grid sm:grid-cols-3 gap-6">
            {contactCards.map(({ icon, title, info, lines }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="rounded-2xl border p-6 backdrop-blur-md hover:shadow-md transition-all duration-300 group"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-colors"
                  style={{ background: 'var(--muted)', color: 'var(--foreground)' }}>
                  {icon}
                </div>
                <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--foreground)' }}>{title}</h3>
                <p className="text-[11px] mb-3" style={{ color: 'var(--muted-foreground)' }}>{info}</p>
                {lines.map((l) => (
                  <p key={l} className="text-xs font-semibold leading-relaxed" style={{ color: 'var(--foreground)' }}>{l}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form & FAQs Section (Separated visually) */}
      <section className="py-20" style={{ background: 'var(--muted)' }}>
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Contact Form */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight mb-2" style={{ color: 'var(--foreground)' }}>Send Us a Message</h2>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Fill out the form below and we will get back to you shortly.</p>
              </div>

              {submitted && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                  className="text-xs font-semibold px-4 py-3 rounded-xl border"
                  style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#16a34a' }}>
                  Thank you! Your message has been sent successfully.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="rounded-2xl border p-6 space-y-4 backdrop-blur-md"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-text">Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                      required placeholder="Your name" className="input-field" />
                  </div>
                  <div>
                    <label className="label-text">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                      required placeholder="you@example.com" className="input-field" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-text">Subject</label>
                    <input type="text" name="subject" value={formData.subject} onChange={handleInputChange}
                      required placeholder="How can we help?" className="input-field" />
                  </div>
                  <div>
                    <label className="label-text">Type</label>
                    <select name="feedbackType" value={formData.feedbackType} onChange={handleInputChange} className="input-field">
                      <option value="general">General Inquiry</option>
                      <option value="feedback">Feedback</option>
                      <option value="support">Technical Support</option>
                      <option value="complaint">Complaint</option>
                      <option value="suggestion">Suggestion</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label-text">Message</label>
                  <textarea name="message" value={formData.message} onChange={handleInputChange}
                    required rows={4} placeholder="Describe your inquiry in detail..." className="input-field resize-none" />
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" className="btn-primary text-xs py-2.5 px-5 flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" /> Send Message
                  </button>
                </div>
              </form>
            </div>

            {/* FAQs */}
            <div className="space-y-6">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  <HelpCircle className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>Frequently Asked Questions</h2>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Quick answers to common questions</p>
                </div>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border p-4 transition-all duration-200 cursor-pointer backdrop-blur-md"
                    style={{
                      background: 'var(--card)',
                      borderColor: activeFaq === i ? 'var(--foreground)' : 'var(--border)',
                    }}
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <p className="font-bold text-xs" style={{ color: 'var(--foreground)' }}>{faq.q}</p>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                        activeFaq === i ? 'rotate-180' : ''
                      }`} style={{ color: 'var(--muted-foreground)' }} />
                    </div>
                    
                    <AnimatePresence initial={false}>
                      {activeFaq === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <p className="mt-3 text-xs leading-relaxed border-t pt-3"
                            style={{ color: 'var(--muted-foreground)', borderColor: 'var(--border)' }}>
                            {faq.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;